import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildDemoOrdersDailyBundle } from '../data/mockOrdersDaily';
import { fetchOrdersDailyBundle } from '../services/ordersDailyTruthClient';
import type {
  OrdersDailyProjectionSource,
  OrdersDailyRow,
  OrdersDailySummary,
  UnscheduledOpenLeg,
} from '../types/ordersDailyTruth';
import { clampOrdersDailyRange } from '../utils/ordersDailyPresentation';

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError')
    || (err instanceof Error && err.name === 'AbortError')
  );
}

/**
 * Wallboard cadence. This is how often the BROWSER re-asks ZeeCore for the
 * figures — deliberately independent of how often ZeeCore polls Palletways
 * upstream. Rendering the dashboard must never trigger vendor API calls, so
 * this interval can be tuned for the operations-room screen without any
 * regard to Palletways rate limits.
 */
export const ORDERS_DAILY_REFRESH_MS = 5 * 60 * 1000;

/**
 * Figures and rows come from one response (includeSummary=1): today's and
 * yesterday's from a stored snapshot rebuilt after new data lands, anything
 * else (or a snapshot that no longer matches) from a live Iceberg read.
 *
 * Refreshes itself every 5 minutes so the screen can live on an office wall
 * without anyone pressing a button; manual refresh remains as a fallback.
 * Polling pauses while the tab is hidden and fires once on re-show, so a
 * backgrounded wallboard neither burns queries nor comes back stale.
 *
 * Pass `enabled: false` to skip network (e.g. Overview when Palletways rates
 * are not the org's Orders path). Pass `demo: true` for admin Demo toggle —
 * same wallboard UI, offline fixtures (no Iceberg call).
 *
 * Pass `includeBacklog: true` to also fetch every currently-open leg
 * regardless of the date range (overdue and never-dated "planned" legs the
 * date-range gate would otherwise hide entirely) — see
 * `zeecore/docs/projections/orders_daily.md#open-backlog-includebacklog--v03`.
 * Only meaningful for a range that includes today; the caller decides when
 * to ask for it (a pure historical day view shouldn't pay for the wider scan).
 *
 * Stale legs (open, long past their date) come back only with
 * `includeStale: true` as well, because finding them all means a much wider
 * scan. Turning it on for the same range reloads in the background, so
 * today's figures stay on screen while the stale legs load.
 */
export function useOrdersDailyProjection(
  startDate: string,
  endDate: string,
  opts?: {
    enabled?: boolean;
    demo?: boolean;
    includeBacklog?: boolean;
    includeStale?: boolean;
    /**
     * Wallboard 5-minute poll. Pass false for secondary fetches (e.g. trend
     * lookback) so the parent drives refresh from the primary hook’s cadence.
     */
    autoRefresh?: boolean;
  },
) {
  const enabled = opts?.enabled !== false;
  const demo = opts?.demo === true;
  const includeBacklog = opts?.includeBacklog === true;
  const includeStale = includeBacklog && opts?.includeStale === true;
  const autoRefresh = opts?.autoRefresh !== false;
  const range = useMemo(
    () => clampOrdersDailyRange(startDate, endDate),
    [startDate, endDate],
  );

  const [summary, setSummary] = useState<OrdersDailySummary | null>(null);
  const [rows, setRows] = useState<OrdersDailyRow[]>([]);
  const [unscheduled, setUnscheduled] = useState<UnscheduledOpenLeg[]>([]);
  const [overdue, setOverdue] = useState<OrdersDailyRow[]>([]);
  const [cancelled, setCancelled] = useState<OrdersDailyRow[]>([]);
  const [stale, setStale] = useState<OrdersDailyRow[]>([]);
  // Start true when enabled so the first paint is "Loading", not a false empty state.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);
  const [projection, setProjection] = useState<OrdersDailyProjectionSource | null>(null);
  // The server is building this day's snapshot; a retry is scheduled.
  const [preparing, setPreparing] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const retryRef = useRef<number | null>(null);

  /**
   * `background: true` keeps the current figures on screen while the next poll
   * is in flight — a wallboard must not blink back to skeletons every five
   * minutes. A failed background poll also leaves the last good numbers up,
   * captioned by a stale "data current as of" clock, rather than blanking the
   * board on one bad request.
   */
  const load = useCallback(async (loadOpts?: { background?: boolean }) => {
    if (!enabled) {
      setSummary(null);
      setRows([]);
      setUnscheduled([]);
      setOverdue([]);
      setStale([]);
      setError(null);
      setLoading(false);
      return;
    }
    const background = loadOpts?.background === true;
    if (retryRef.current !== null) {
      window.clearTimeout(retryRef.current);
      retryRef.current = null;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    if (!background) setLoading(true);
    setError(null);
    try {
      if (demo) {
        const bundle = buildDemoOrdersDailyBundle(range.startDate, range.endDate);
        if (ac.signal.aborted) return;
        setSummary(bundle.summary);
        setRows(bundle.records);
        setUnscheduled([]);
        setOverdue([]);
        setStale([]);
        setLastLoadedAt(new Date().toISOString());
        return;
      }
      const bundle = await fetchOrdersDailyBundle(range.startDate, range.endDate, {
        signal: ac.signal,
        includeBacklog,
        includeStale,
      });
      if (ac.signal.aborted) return;
      if ('preparing' in bundle) {
        // Keep whatever is on screen; ask again once the snapshot should exist.
        setPreparing(true);
        retryRef.current = window.setTimeout(() => {
          retryRef.current = null;
          void loadRef.current({ background: true });
        }, bundle.retryAfterSeconds * 1000);
        return;
      }
      setPreparing(false);
      setProjection(bundle.projection ?? null);
      setSummary(bundle.summary);
      setRows(bundle.records);
      setUnscheduled(bundle.unscheduled);
      setOverdue(bundle.overdue);
      setStale(bundle.stale);
      setCancelled(bundle.cancelled ?? []);
      // A stored snapshot's figures are as of when it was computed, not when we fetched it.
      setLastLoadedAt(bundle.projection?.computed_at ?? new Date().toISOString());
    } catch (err) {
      if (isAbortError(err) || ac.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load order figures');
      if (!background) {
        setSummary(null);
        setRows([]);
        setUnscheduled([]);
        setOverdue([]);
        setStale([]);
        setCancelled([]);
      }
    } finally {
      if (!ac.signal.aborted && !background) setLoading(false);
    }
  }, [demo, enabled, includeBacklog, includeStale, range.startDate, range.endDate]);

  // Everything that decides the figures except the stale opt-in. When only
  // that opt-in changed, reload in the background: the figures on screen are
  // still right, only the stale section is being added.
  const figuresKey = `${demo}|${enabled}|${includeBacklog}|${range.startDate}|${range.endDate}`;
  /** Demo / enabled — changing these must drop on-screen figures (fixtures must not linger). */
  const sourceKey = `${demo}|${enabled}`;
  const loadedFiguresKeyRef = useRef<string | null>(null);
  const loadedSourceKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const previousFiguresKey = loadedFiguresKeyRef.current;
    const previousSourceKey = loadedSourceKeyRef.current;
    const background = previousFiguresKey === figuresKey;
    loadedFiguresKeyRef.current = figuresKey;
    loadedSourceKeyRef.current = sourceKey;
    if (!background) {
      setPreparing(false);
      setProjection(null);
      // Only wipe figures when the data *source* changes (demo↔live or enable
      // gate). Date-range / backlog changes keep the last figures on screen
      // until the new load lands — wallboard-friendly, no blank flash.
      const sourceChanged = previousSourceKey !== sourceKey;
      if (sourceChanged) {
        setSummary(null);
        setRows([]);
        setUnscheduled([]);
        setOverdue([]);
        setStale([]);
        setLastLoadedAt(null);
        setError(null);
      }
    }
    void load({ background });
    return () => {
      abortRef.current?.abort();
      if (retryRef.current !== null) window.clearTimeout(retryRef.current);
    };
  }, [load, figuresKey, sourceKey]);

  // `load` is recreated whenever the date range changes; keep a ref to the
  // latest one so the interval effect below can depend on nothing and never
  // gets torn down and rebuilt. A timer effect keyed on `load` would restart
  // its 5-minute countdown from zero on every such change — on a wallboard
  // left open across a date rollover (or any re-render that touches the
  // range) the clock would never reach 5 minutes and auto-refresh would
  // silently stop firing.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  // Wallboard auto-refresh. Skipped while hidden; one catch-up poll on re-show.
  // Demo mode still refreshes so "data current as of" ticks without hitting Iceberg.
  // Secondary consumers (trend lookback) pass autoRefresh: false and piggy-back
  // on the primary hook’s lastLoadedAt / Refresh button instead.
  useEffect(() => {
    if (!enabled || !autoRefresh) return;
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      void loadRef.current({ background: true });
    };
    const timer = window.setInterval(tick, ORDERS_DAILY_REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [enabled, autoRefresh]);

  return {
    range,
    summary,
    rows,
    unscheduled,
    overdue,
    stale,
    /** Legs cancelled in their source: shown in the orders table as Cancelled, in no figure. */
    cancelled,
    /** Stale legs were asked for and the response carrying them has not landed yet. */
    staleLoading: includeStale && summary?.stale?.loaded === false,
    rowsLoading: loading,
    loading,
    error,
    lastLoadedAt,
    /** Where the figures came from, and if stored and out of date, why. */
    projection,
    /** The server is building this day's figures; a retry is scheduled. */
    preparing,
    refresh: load,
    refreshIntervalMs: ORDERS_DAILY_REFRESH_MS,
  };
}
